import { ElementRef, inject, Injectable } from '@angular/core';
import { AuthService } from '@core/auth.service';

export interface Addon {
  offer_id: string | number;
  value: number;
  product_description: string;
  preferred_currency: { Value: number; currency: string };
  is_default?: number;
  price?: Record<string, number>;
  html_msg?: string;
  [key: string]: unknown;
}

export interface PurchaseDetail {
  offers?: {
    add_on?: Addon[];
    int_price?: Record<string, number>;
    price?: number | string;
    currency?: string;
    offerid?: string;
    sticky_iframe?: string;
    horizontal_upsells?: string;
    description?: string | string[];
    [key: string]: unknown;
  };
  prosub_id?: string;
  p_type?: string | number;
  addons?: Addon[];
  prize?: number | string;
  [key: string]: unknown;
}

export interface SafeStorage {
  getItem(key: string): string | null;
}

export interface CheckBoxResult {
  addons: Addon[];
  checkedValues: number[];
  checkedValuesDefaultCurrency: number[];
  addonTotalAmount: number;
}

export interface UpsellDataResult {
  preferredCurrency: string;
  addons: Addon[];
  totalOfferPrizeValue: number;
  purchaseDetail: PurchaseDetail;
}

export interface CheckBoxValueConfig {
  checkboxConfigs: Array<{
    index: number;
    priceValue: number;
  }>;
}

@Injectable()
export class CardUpsellService {
    private authService = inject(AuthService);

    async modifyAddonContent(details: PurchaseDetail): Promise<PurchaseDetail> {
        const addOns = details?.offers?.add_on;
        if (!addOns?.length) return details;

        const updatedAddOns: Addon[] = await Promise.all(
            addOns.map(async (element: Addon): Promise<Addon> => {
                if (!element?.html_msg) return element;

                try {
                    const dvString = await this.authService.getDVFromString(element.html_msg);
                    return dvString ? { ...element, html_msg: String(dvString) } : element;
                } catch (err) {
                    this.authService.errorHandlerLogs('Failed to process addon html_msg:', err);
                    return element;
                }
            })
        );

        return {
            ...details,
            offers: {
                ...details.offers,
                add_on: updatedAddOns
            }
        };
    }

  // AC-5: pure input/output — no PaymentPage dependency; host applies returned result
  public checkBoxChanged(event: { detail?: { checked?: boolean; value?: number } }, offerData: Addon, ctx: {
    purchaseDetail: PurchaseDetail;
    preferredCurrency: string;
    checkedValues: number[];
    checkedValuesDefaultCurrency: number[];
  }): CheckBoxResult {
    const isChecked = event?.detail?.checked;
    const prizeValue = Number(event?.detail?.value ?? 0);
    const prizeValueDefault = Number(offerData?.price?.['GBP'] ?? 0);
    const offerPrizeValue = Number(ctx.purchaseDetail?.offers?.int_price?.[ctx.preferredCurrency] ?? 0);
    let selectedOffers: Addon[] = [...(ctx.purchaseDetail.addons ?? [])];
    const checkedValues = [...ctx.checkedValues];
    const checkedValuesDefaultCurrency = [...ctx.checkedValuesDefaultCurrency];

    if (isChecked) {
      if (selectedOffers.findIndex((o) => o.offer_id === offerData.offer_id) === -1) {
        checkedValues.push(prizeValue);
        checkedValuesDefaultCurrency.push(prizeValueDefault);
        selectedOffers.push({
          offer_id: offerData.offer_id,
          value: Number(offerData.price?.['GBP'] ?? 0),
          product_description: offerData?.product_description || 'offer',
          preferred_currency: { Value: Number(offerData.price?.[ctx.preferredCurrency] ?? 0), currency: ctx.preferredCurrency },
        });
      }
    } else {
      const idx = checkedValues.indexOf(prizeValue);
      if (idx !== -1) { checkedValues.splice(idx, 1); checkedValuesDefaultCurrency.splice(idx, 1); }
      selectedOffers = selectedOffers.filter((o) => o.offer_id !== offerData.offer_id);
    }

    const addonTotalAmount = Number(
      (checkedValues.reduce((acc, v) => acc + Number(v), 0) + offerPrizeValue).toFixed(2)
    );
    return { addons: selectedOffers, checkedValues, checkedValuesDefaultCurrency, addonTotalAmount };
  }

  // AC-6: Set default upsell data — returns structured output instead of mutating context
  public setDefaultUpsellData(ctx: {
    isDestroyed: boolean;
    safeLocalStorage: SafeStorage;
    preferredCurrency: string;
    purchaseDetail: PurchaseDetail;
    upsellList: unknown[];
    p_id: number;
  }): UpsellDataResult | null {
    if (ctx.isDestroyed) return null;

    const preferredCurrency = ctx.safeLocalStorage.getItem('user_currency') || ctx.preferredCurrency || 'GBP';

    try {
      const addons: Addon[] = [];

      if (ctx.upsellList?.length > 0 && ctx.purchaseDetail?.p_type == ctx.p_id) {
        return { preferredCurrency, addons: [], totalOfferPrizeValue: 0, purchaseDetail: ctx.purchaseDetail };
      }

      const totalOfferPrizeValue = (ctx.purchaseDetail?.offers?.add_on ?? [])
        .filter((value) => {
          if (value.is_default) {
            addons.push({
              offer_id: value.offer_id,
              value: Number(value.price?.['GBP'] ?? 0),
              product_description: value?.product_description || 'offer',
              preferred_currency: {
                Value: Number(value.price?.[preferredCurrency] ?? 0),
                currency: preferredCurrency,
              },
            });
            return true;
          }
          return false;
        })
        .reduce((total, value) => total + Number(value.price?.[preferredCurrency] ?? 0), 0);

      return {
        preferredCurrency,
        addons,
        totalOfferPrizeValue,
        purchaseDetail: { ...ctx.purchaseDetail, addons },
      };
    } catch (err) {
      this.authService.errorHandlerLogs('Error in setDefaultUpsellData:', err);
      return null;
    }
  }

  // AC-7: Get checkbox configuration from slides — returns structured config instead of mutating DOM
  public getCheckBoxValue(ctx: {
    el: ElementRef<HTMLElement>;
    purchaseDetail: PurchaseDetail;
    preferredCurrency: string;
  }): CheckBoxValueConfig {
    const nativeEl: HTMLElement | undefined = ctx?.el?.nativeElement;
    if (!nativeEl) {
      return { checkboxConfigs: [] };
    }

    const slides: NodeListOf<HTMLElement> = nativeEl.querySelectorAll('.bonus-slide');
    const checkboxConfigs: CheckBoxValueConfig['checkboxConfigs'] = [];

    slides.forEach((slide: HTMLElement, index: number) => {
      const checkbox = slide.querySelector('ion-checkbox');
      if (checkbox) {
        const priceValue = Number(ctx.purchaseDetail?.offers?.add_on?.[index]?.price?.[ctx.preferredCurrency] ?? 0);
        checkboxConfigs.push({ index, priceValue });
      } else {
        this.authService.errorHandlerLogs(`No ion-checkbox found in slide at index ${index}.`);
      }
    });

    return { checkboxConfigs };
  }
}
